import { useCallback, useState } from 'react';
import {
    getMonitoredAudio,
    MonitoredAudioTrackingTypes,
    MonitoredAudioUrl,
    patchMonitoredAudioTrackingType,
} from '@round/api';
import useAbortableEffect from '../../../../../../Hooks/useAbortableEffect';
import { useCheckUserGroupsAccess } from '../../../../../Auth/hooks/useCheckUserGroupsAccess';
import { showNotification } from '../../../../../../helpers';

export default function useMonitoredAudio(monitoredAudioId: number | undefined) {
    const hasUserAdminPermissions = useCheckUserGroupsAccess(['user_admin']);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [monitoredAudio, setMonitoredAudio] = useState<MonitoredAudioUrl | null>(null);

    const [isUpdateTrackingTypeLoading, setIsUpdateTrackingTypeLoading] = useState(false);

    const fetchMonitoredAudio = useCallback(
        async (requestInit?: RequestInit) => {
            if (!hasUserAdminPermissions || !monitoredAudioId) {
                return;
            }

            try {
                setError(null);
                setIsLoading(true);
                const response = await getMonitoredAudio(monitoredAudioId, requestInit);
                if (response.status === 404) {
                    setError(response.data.detail);
                    return;
                }

                setMonitoredAudio(response.data);
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                setError((e as Error).message);
            } finally {
                setIsLoading(false);
            }
        },
        [hasUserAdminPermissions, monitoredAudioId]
    );

    const abortController = useAbortableEffect(
        (signal) => {
            fetchMonitoredAudio({ signal });
        },
        [fetchMonitoredAudio]
    );

    const reset = useCallback(() => {
        setMonitoredAudio(null);
        setIsLoading(false);
        setError(null);
    }, []);

    const updateMonitoredAudioTrackingType = useCallback(
        async (trackingType: MonitoredAudioTrackingTypes, trackingTypeOverrideUntil?: string) => {
            if (!monitoredAudio) {
                return;
            }

            try {
                setIsUpdateTrackingTypeLoading(true);
                const response = await patchMonitoredAudioTrackingType(monitoredAudio.id, {
                    tracking_type: trackingType,
                    tracking_type_override_until: trackingTypeOverrideUntil,
                });
                if (response.status === 200) {
                    showNotification('Tracking type updated', 'info');
                    setMonitoredAudio({
                        ...monitoredAudio,
                        is_enhanced: response.data.is_enhanced,
                        tracking_type: response.data.tracking_type,
                        // The deadline is write-only server-side, so reflect what we just sent:
                        // the chosen window when enhancing, cleared otherwise.
                        tracking_type_override_until:
                            response.data.tracking_type === MonitoredAudioTrackingTypes.Enhanced
                                ? trackingTypeOverrideUntil ?? null
                                : null,
                    });
                    return;
                }

                if (response.status === 404 || response.status === 400) {
                    showNotification('Could not update tracking type', 'error');
                    return;
                }
            } catch {
                showNotification('Could not update tracking type', 'error');
            } finally {
                setIsUpdateTrackingTypeLoading(false);
            }
        },
        [monitoredAudio]
    );

    return {
        monitoredAudio,
        isLoading,
        error,
        reset,
        abortController,
        isUpdateTrackingTypeLoading,
        updateMonitoredAudioTrackingType,
    };
}
